import { ChangeEventHandler, useState } from 'react'

import { isEqual } from 'lodash-es'
import { useTranslation } from 'react-i18next'
import { SingleValue } from 'react-select'
import { connect, useDataItem } from 'scrivito'

import { StaticSublistContent } from './SubComponents/StaticSublistContent'
import { ExportCsvModal } from '../ListPersonTab/ExportCsvModal'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Button } from '@/Atomic/Molecules/Button'
import { Search } from '@/Atomic/Molecules/Inputs/Search'
import { Option } from '@/Atomic/Molecules/Inputs/Select/types'
import { SimpleSelect } from '@/Atomic/Molecules/SimpleSelect'
import { ScrollablePanel } from '@/Atomic/Templates/ScrollablePanel'
import { Sublist } from '@/Atomic/Templates/Sublist'
import { DataItemDrawer } from '@/Atomic/Widgets/DataItemDrawer'
import { CSVModal } from '@/Atomic/Widgets/Imports/CSVModal'
import { ImportStatus } from '@/Atomic/Widgets/Imports/CSVModal/useCsvModal'
import {
  CONTACT_ASC_ORDER,
  CONTACT_DESC_ORDER,
  DataClassName,
} from '@/Data/const'
import { DataScopeOrder } from '@/Data/types'
import { useDataItemDrawer } from '@/utils/CRUD/hooks/useDataItemDrawer'

interface StaticListContactTabProps {
  contactEntity: DataClassName.COMPANY | DataClassName.PERSON
}

export const StaticListContactTab: React.FC<StaticListContactTabProps> =
  connect(({ contactEntity }) => {
    const { t } = useTranslation()

    const dataItem = useDataItem()
    const dataItemId = dataItem?.id() ?? ''

    const [searchContact, setSearchContact] = useState('')

    const [isCSVModalOpen, setIsCSVModalOpen] = useState(false)
    const [isExportModalOpen, setIsExportModalOpen] = useState(false)
    const [csvInitialFile, setCsvInitialFile] = useState<File | null>(null)
    const [isImporting, setIsImporting] = useState(false)

    const { drawerItem, setDrawerItem, isDrawerOpen, openDrawer, closeDrawer } =
      useDataItemDrawer()

    const openCsvModal = (file?: File) => {
      setCsvInitialFile(file ?? null)
      setIsCSVModalOpen(true)
    }

    const closeCsvModal = () => {
      setIsCSVModalOpen(false)
      setCsvInitialFile(null)
    }

    const openExportModal = () => {
      setIsExportModalOpen(true)
    }

    const closeExportModal = () => {
      setIsExportModalOpen(false)
    }

    const handleImportStatusChange = (status: ImportStatus) => {
      setIsImporting(status === 'running')
    }

    const onSearchChange: ChangeEventHandler<HTMLInputElement> = (event) => {
      setSearchContact(event.target.value)
    }

    // Filter option - Order
    const [order, setOrder] = useState<DataScopeOrder>(CONTACT_DESC_ORDER)
    const orderOptions: Option[] = [
      {
        label: t('common.sort.recentFirst'),
        value: 'desc',
      },
      {
        label: t('common.sort.recentLast'),
        value: 'asc',
      },
    ]

    const currentOrderValue = isEqual(order, CONTACT_DESC_ORDER)
      ? 'desc'
      : 'asc'
    const selectedOrderOption = orderOptions.find(
      (o) => o.value === currentOrderValue,
    )

    const handleOrderChange = (newValue: SingleValue<Option | unknown>) => {
      const val = (newValue as Option)?.value
      if (val === 'asc') {
        setOrder(CONTACT_ASC_ORDER)
      } else {
        setOrder(CONTACT_DESC_ORDER)
      }
    }

    if (!dataItemId) return

    const header = (
      <div className="tab-header-wrapper">
        <div className="search-filter">
          <Search
            name="search sublist"
            onChange={onSearchChange}
            placeholder={t('common.search')}
          />
          <StringText value={t('common.sortBy')} />
          <SimpleSelect
            name="order-select"
            className="small"
            options={orderOptions}
            value={selectedOrderOption}
            onChange={handleOrderChange}
          />
        </div>
        <div className="tab-header-actions">
          <Button
            icon="download-circle"
            style={{ height: '32px' }}
            onClick={openExportModal}
          >
            {t('components.csv.exportTitle')}
          </Button>
          <Button
            icon="upload"
            variant="outlined"
            style={{ height: '32px' }}
            onClick={() => openCsvModal()}
          >
            {t('components.csv.importTitle')}
          </Button>
        </div>
      </div>
    )

    return (
      <>
        <Sublist>
          <ScrollablePanel header={header}>
            <div className="sublist-wrapper">
              {!isImporting && (
                <StaticSublistContent
                  dataItemId={dataItemId}
                  contactEntity={contactEntity}
                  searchContact={searchContact}
                  order={order}
                  onOpenCsvModal={openCsvModal}
                  onOpenDrawer={(person) => {
                    setDrawerItem(person)
                    openDrawer()
                  }}
                />
              )}
            </div>
          </ScrollablePanel>
        </Sublist>
        <CSVModal
          isOpen={isCSVModalOpen}
          context="list"
          listId={dataItemId}
          entityName={contactEntity}
          initialFile={csvInitialFile ?? undefined}
          onClose={closeCsvModal}
          onImportStatusChange={handleImportStatusChange}
        />
        <ExportCsvModal
          isOpen={isExportModalOpen}
          listId={dataItemId}
          contactEntity={contactEntity}
          listTitle={dataItem?.get('title') as string}
          onClose={closeExportModal}
        />
        {drawerItem && (
          <DataItemDrawer
            key={drawerItem.id()}
            isOpen={isDrawerOpen}
            close={closeDrawer}
            dataItem={drawerItem}
          />
        )}
      </>
    )
  })
